import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { ProductBundleApiService } from '../services/product-bundle-api.service';
import { AuthService } from '../../auth-pages/services/auth.service';
import { DatetimeService } from '../../../shared/services/datetime.service';
import { AuditApiService } from '../../audit/services/audit-api.service';
import { AuditLogResponse } from '../../audit/models/audit.model';
import { AuditDisplayService } from '../../../shared/services/audit-display.service';
import {
  ProductBundleResponse,
  ProductBundleItemResponse,
} from '../models/product-bundle.model';
import { CurrencyResponse } from '../models/producto.model';
import { ProductoApiService } from '../services/producto-api.service';
import { AuditTimelineComponent } from '../../../shared/components/audit/audit-timeline.component';
import { currencyFractionDigits } from '../../../shared/utils/currency-format.utils';

type BadgeColor =
  | 'primary'
  | 'success'
  | 'error'
  | 'warning'
  | 'info'
  | 'light'
  | 'dark';

@Component({
  selector: 'app-product-bundle-detail-modal',
  standalone: true,
  imports: [
    CommonModule,
    ModalComponent,
    BadgeComponent,
    AuditTimelineComponent,
  ],
  templateUrl: './product-bundle-detail-modal.component.html',
})
export class ProductBundleDetailModalComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() bundle: ProductBundleResponse | null = null;
  @Output() close = new EventEmitter<void>();

  bundleItems: ProductBundleItemResponse[] = [];
  loadingItems = false;

  // Audit timeline
  activeTab: 'info' | 'timeline' = 'info';
  timelineLogs: AuditLogResponse[] = [];
  loadingTimeline = false;
  timelineError = '';
  timelinePage = 0;
  timelineTotalPages = 0;
  timelineTotalElements = 0;

  businessId = 1;
  monedas: CurrencyResponse[] = [];
  productMap: Record<number, { name: string; price: number }> = {};

  constructor(
    private bundleApi: ProductBundleApiService,
    private authService: AuthService,
    private productApi: ProductoApiService,
    private datetimeService: DatetimeService,
    private auditApi: AuditApiService,
    private auditDisplay: AuditDisplayService,
  ) {
    const user = this.authService.getCurrentUser();
    this.businessId = user?.businessId || 1;
    this.cargarMonedas();
  }

  /**
   * Inc 58: reacciona también a un cambio de `bundle` con el modal ya abierto y
   * resetea los flags de carga, que antes podían quedar colgados en true.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (!(changes['isOpen'] || changes['bundle'])) return;
    if (!this.isOpen || !this.bundle) return;

    // Inc 58: businessId real del usuario (el `|| 1` provocaba 403 en otros negocios).
    this.businessId = this.authService.getBusinessId() ?? this.businessId;
    this.activeTab = 'info';
    this.bundleItems = [];
    this.loadingItems = false;
    this.timelineLogs = [];
    this.productMap = {};
    this.timelinePage = 0;
    this.timelineTotalPages = 0;
    this.timelineError = '';
    this.cargarItems();
  }

  private cargarMonedas(): void {
    this.productApi.getCurrencies().subscribe({
      next: (data) => (this.monedas = data),
      error: () => {},
    });
  }

  private cargarItems(): void {
    if (!this.bundle) return;
    this.loadingItems = true;
    this.bundleApi.getBundleItems(this.bundle.id, this.businessId).subscribe({
      next: (items) => {
        this.bundleItems = items;
        this.loadingItems = false;
        this.cargarProductosDeItems();
      },
      error: () => {
        this.bundleItems = [];
        this.loadingItems = false;
      },
    });
  }

  private cargarProductosDeItems(): void {
    const ids = [...new Set(this.bundleItems.map((it) => it.idProduct))];
    ids.forEach((id) => {
      if (this.productMap[id]) return;
      this.productApi.getProductById(id, this.businessId).subscribe({
        next: (p) => {
          this.productMap[id] = { name: p.name, price: p.price ?? 0 };
        },
        error: () => {
          this.productMap[id] = { name: `Producto #${id}`, price: 0 };
        },
      });
    });
  }

  getLogDotColor(op: string): string {
    switch (op?.toUpperCase()) {
      case 'CREATE':
        return 'bg-success-500';
      case 'UPDATE':
        return 'bg-brand-500';
      case 'DELETE':
        return 'bg-error-500';
      default:
        return 'bg-gray-400';
    }
  }

  onClose(): void {
    this.close.emit();
  }

  getActiveColor(): BadgeColor {
    return this.bundle?.isActive ? 'success' : 'error';
  }
  getActiveLabel(): string {
    return this.bundle?.isActive ? 'Activo' : 'Inactivo';
  }

  formatPrecio(price?: number, currencyCode?: string): string {
    if (price === undefined || price === null) return '-';
    const code = currencyCode || 'USD';
    try {
      // Sin minimumFractionDigits: Intl ya usa la unidad mínima ISO 4217 de la
      // moneda (0 en JPY, 2 en USD, 3 en KWD). Forzar 2 pintaba importes
      // inexistentes en las monedas sin céntimos.
      return new Intl.NumberFormat('es-MX', {
        style: 'currency',
        currency: code,
      }).format(price);
    } catch {
      return `${code} ${price.toFixed(currencyFractionDigits(code))}`;
    }
  }

  formatDate(dateString?: string): string {
    return this.datetimeService.formatDateTime(dateString);
  }

  getCurrencyCode(currencyId?: number): string {
    if (!currencyId) return 'USD';
    return this.monedas.find((m) => m.id === currencyId)?.code || 'USD';
  }

  //detalles en el timeline
  private readonly FIELD_LABELS: Record<string, string> = {
    name: 'Nombre',
    description: 'Descripción',
    sku: 'SKU',
    bundlePrice: 'Precio bundle',
    discountPercent: 'Descuento %',
    currencyId: 'Moneda',
    imageUrl: 'Imagen',
    isActive: 'Estado activo',
    priceStrategy: 'Estrategia de precio',
    displayMode: 'Modo de visualización',
    bundleRole: 'Rol del paquete',
    calculatedPrice: 'Precio calculado',
  };

  private readonly HIDDEN_FIELDS = [
    'id',
    'ref',
    'businessId',
    'datetimeCreated',
    'datetimeUpdated',
  ];

  private readonly PRICE_STRATEGY_LABELS: Record<string, string> = {
    fixed: 'Precio fijo',
    sum_of_components: 'Suma de componentes',
    percentage_discount: 'Descuento porcentual',
    manual: 'Manual',
  };

  private readonly DISPLAY_MODE_LABELS: Record<string, string> = {
    breakdown: 'Desglosado',
    grouped: 'Agrupado',
    combined: 'Combinado',
    single_line: 'Línea única',
    single: 'Único',
  };

  private readonly BUNDLE_ROLE_LABELS: Record<string, string> = {
    standard: 'Estándar',
    primary: 'Principal',
    addon: 'Complemento',
    optional: 'Opcional',
  };

  private readonly BOOLEAN_FIELDS = ['isActive', 'isOptional'];
  private readonly PERCENT_FIELDS = ['discountPercent'];
  private readonly MONEY_FIELDS = ['bundlePrice', 'calculatedPrice'];

  getUserLabel(log: AuditLogResponse): string {
    if (log.userFullName) return log.userFullName;
    if (log.username) return log.username;
    return `Usuario #${log.idUser}`;
  }

  getChangedValues(
    log: AuditLogResponse,
  ): Array<{ field: string; label: string; old: string; new: string }> {
    // Preferir el enriquecimiento del backend (changesDisplay); si es null, fallback técnico propio.
    const enriched = this.auditDisplay.displayRows(log);
    if (enriched !== null) {
      return enriched.map((r) => ({
        field: r.field,
        label: r.label,
        old: r.oldVal,
        new: r.newVal,
      }));
    }
    try {
      const oldObj =
        typeof log.oldValues === 'string'
          ? JSON.parse(log.oldValues)
          : log.oldValues || {};
      const newObj =
        typeof log.newValues === 'string'
          ? JSON.parse(log.newValues)
          : log.newValues || {};

      const fields: string[] =
        Array.isArray(log.changedFields) && log.changedFields.length > 0
          ? log.changedFields
          : Object.keys(newObj).filter(
              (k) => JSON.stringify(oldObj[k]) !== JSON.stringify(newObj[k]),
            );

      return fields
        .filter((f) => !this.HIDDEN_FIELDS.includes(f))
        .map((f) => ({
          field: f,
          label: this.FIELD_LABELS[f] || f,
          old: this.formatFieldValue(f, oldObj[f]),
          new: this.formatFieldValue(f, newObj[f]),
        }))
        .filter((f) => f.old !== f.new);
    } catch {
      return [];
    }
  }

  // snake_case desconocido → "Snake Case"
  private prettify(value: string): string {
    return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  getPriceStrategyLabel(v?: string): string {
    if (!v) return '-';
    return this.PRICE_STRATEGY_LABELS[v] ?? this.prettify(v);
  }

  getDisplayModeLabel(v?: string): string {
    if (!v) return '-';
    return this.DISPLAY_MODE_LABELS[v] ?? this.prettify(v);
  }

  getBundleRoleLabel(v?: string): string {
    if (!v) return '-';
    return this.BUNDLE_ROLE_LABELS[v] ?? this.prettify(v);
  }

  //valores para el timeline
  private formatFieldValue(field: string, value: unknown): string {
    if (value === undefined || value === null || value === '') return '-';

    // Booleanos → Sí / No
    if (this.BOOLEAN_FIELDS.includes(field) || typeof value === 'boolean') {
      return value === true || value === 'true' ? 'Sí' : 'No';
    }

    // Moneda: ID → código (USD, EUR...)
    if (field === 'currencyId') return this.getCurrencyCode(Number(value));

    // Enums conocidos
    if (field === 'priceStrategy')
      return this.getPriceStrategyLabel(String(value));
    if (field === 'displayMode') return this.getDisplayModeLabel(String(value));
    if (field === 'bundleRole') return this.getBundleRoleLabel(String(value));

    // Porcentajes
    if (this.PERCENT_FIELDS.includes(field)) return `${value}%`;

    // Montos
    if (this.MONEY_FIELDS.includes(field))
      return this.formatPrecio(Number(value));

    return String(value);
  }

  // ── Inc 57: vigencia promocional del paquete ──

  /**
   * ¿Se debe mostrar el precio promocional en lugar del precio del paquete? Solo
   * con la promoción VIGENTE, ENCENDIDA y con precio (mismo criterio que en
   * Productos): `promoVigente` describe solo la ventana de fechas.
   */
  get promoActiva(): boolean {
    const b = this.bundle;
    return !!(
      b &&
      b.promoVigente &&
      b.promoEnabled !== false &&
      b.promoPrice !== undefined &&
      b.promoPrice !== null
    );
  }

  get tienePromocion(): boolean {
    const b = this.bundle;
    if (!b) return false;
    return !!(
      b.promoValidFrom ||
      b.promoValidUntil ||
      (b.promoPrice !== undefined && b.promoPrice !== null)
    );
  }

  getPromoStatusLabel(): string {
    switch (this.bundle?.promoStatus) {
      case 'active':
        return 'Activa';
      case 'scheduled':
        return 'Programada';
      case 'expired':
        return 'Expirada';
      default:
        return 'Inactiva';
    }
  }

  getPromoStatusColor(): BadgeColor {
    switch (this.bundle?.promoStatus) {
      case 'active':
        return 'success';
      case 'scheduled':
        return 'warning';
      case 'expired':
        return 'error';
      default:
        return 'light';
    }
  }

  /** Fecha sin hora ('YYYY-MM-DD' → 'dd/mm/yyyy'). */
  formatDateOnly(dateStr?: string): string {
    if (!dateStr) return '-';
    const partes = dateStr.split('T')[0].split('-');
    if (partes.length === 3) {
      const [y, m, d] = partes;
      return `${d}/${m}/${y}`;
    }
    return this.datetimeService.formatDateTime(dateStr);
  }

  //
  getProductName(idProduct: number): string {
    return this.productMap[idProduct]?.name ?? `Producto #${idProduct}`;
  }

  // Precio calculado: suma de (precio unitario × cantidad) de cada item
  get calculatedPriceComputed(): number | undefined {
    if (this.bundleItems.length === 0) return undefined;
    // si falta algún precio aún no resuelto, no mostramos un total parcial
    const faltan = this.bundleItems.some(
      (it) => !this.productMap[it.idProduct],
    );
    if (faltan) return undefined;
    return this.bundleItems.reduce(
      (sum, it) =>
        sum + this.productMap[it.idProduct].price * (it.quantity ?? 1),
      0,
    );
  }

  // Usa el del backend si viene, si no el calculado en front
  get precioCalculadoFinal(): number | undefined {
    return this.bundle?.calculatedPrice ?? this.calculatedPriceComputed;
  }

  loadTimeline(page = 0): void {
    if (!this.bundle?.id) return;
    this.loadingTimeline = true;
    this.timelineError = '';
    this.timelinePage = page;

    this.auditApi
      .getLogs({
        entityName: 'product_bundle',
        idEntity: String(this.bundle.id),
        page,
        size: 15,
      })
      .subscribe({
        next: (data) => {
          this.timelineLogs = data.content ?? [];
          this.timelinePage = data.page ?? page;
          this.timelineTotalPages = data.totalPages ?? 1;
          this.timelineTotalElements = data.totalElements ?? 0;
          this.loadingTimeline = false;
        },
        error: () => {
          this.timelineError = 'Error al cargar la línea de tiempo.';
          this.loadingTimeline = false;
        },
      });
  }

  onTabChange(tab: 'info' | 'timeline'): void {
    this.activeTab = tab;
    if (
      tab === 'timeline' &&
      this.timelineLogs.length === 0 &&
      !this.loadingTimeline
    ) {
      this.loadTimeline(0);
    }
  }
}
